/**
 * Status (spec §2 A, §2 C sponsor, §2 L entitlement): on or off, who sponsors
 * it, and whether the plan includes AI at all. Automatic runs read within the
 * sponsor's visibility and stop when the sponsor no longer administers the
 * workspace — the page says so and lets an administrator take it over.
 */

import { useEffect, useState } from 'react';
import { Button } from '../../ui/Button';
import { Callout } from '../../ds';
import { getAgentSponsorStatus, type AgentSponsorStatus } from '../../../lib/agentRuntimeService';
import { isWorkspaceEntitledToAi } from '../../../lib/ai/aiAssistSettings';
import { SwitchGroup, SwitchRow, type SectionProps } from './shared';

export function StatusSection({ def, config, workspaceId, userId, isSaving, canEdit, save }: SectionProps) {
  const [sponsor, setSponsor] = useState<AgentSponsorStatus | null>(null);
  const entitled = isWorkspaceEntitledToAi(workspaceId);
  const enabled = config?.enabled ?? false;

  useEffect(() => {
    let cancelled = false;
    void getAgentSponsorStatus(workspaceId, def.agentType).then((s) => { if (!cancelled) setSponsor(s); });
    return () => { cancelled = true; };
  }, [workspaceId, def.agentType, config?.sponsorUserId, config?.enabled]);

  const needsSponsor = enabled && sponsor != null && !sponsor.sponsorIsAdmin;

  return (
    <SwitchGroup title="Status" lead="Off is absence: nothing shows and nothing runs. Whoever turns it on is its sponsor.">
      {!entitled && (
        <Callout tone="mustard">This workspace's plan does not include AI features, so this agent cannot run.</Callout>
      )}
      <SwitchRow
        label={`${def.name} is on`}
        help="When on, it can be run from an item and from Run now, and its triggers below take effect."
        on={enabled}
        disabled={isSaving || !canEdit || (!entitled && !enabled)}
        onChange={() => void save({ enabled: !enabled }, enabled ? `${def.name} turned off` : `${def.name} turned on`)}
      />
      <div className="border-t border-[var(--rule)] pt-3 space-y-2">
        <p className="text-sm font-medium text-[var(--text-pri)]">Sponsor</p>
        <p className="text-[11px] text-[var(--text-ter)]">
          Automatic runs carry the agent's name and the sponsor's, and read only what the sponsor can see.
        </p>
        <p className="text-sm text-[var(--text-sec)]">{sponsor?.sponsorName ?? (sponsor?.sponsorUserId ? 'Unknown person' : 'No sponsor yet')}</p>
        {needsSponsor && (
          <Callout tone="coral" title="Needs a sponsor">
            The sponsor no longer administers this workspace, so automatic runs have stopped. Manual runs still work.
          </Callout>
        )}
        {canEdit && userId && sponsor?.sponsorUserId !== userId && (
          <Button variant="secondary" size="sm" disabled={isSaving} onClick={() => void save({ sponsorUserId: userId }, 'You are now the sponsor')}>
            Make me the sponsor
          </Button>
        )}
      </div>
    </SwitchGroup>
  );
}
